import { getSupabaseClient } from './supabase.client.js';

/**
 * Repositorio de feriados (`feriados`) para el panel de staff (Control de
 * Horarios y Liquidación de Sueldos, slice 5 — Liquidación). Full CRUD desde
 * el navegador vía RLS admin-only (`feriados_admin_all`, migración 20,
 * `for all using (is_admin()) with check (is_admin())`) — mismo patrón que
 * `empleados.repository.js` sobre `empleados`, a diferencia de
 * `liquidaciones.repository.js` (solo lectura, tabla append-only).
 *
 * Columnas SIEMPRE explícitas (nunca `select('*')`).
 */

const FERIADO_COLUMNS = 'fecha, descripcion, created_at';

/**
 * Re-lanza un error de Supabase preservando el código de Postgres (mismo
 * patrón que el resto de los repositorios de este slice).
 * @param {{ message: string, code?: string }} error
 * @returns {never}
 */
function rethrow(error) {
    const err = new Error(error.message);
    if (error.code) err.code = error.code;
    throw err;
}

/**
 * Todos los feriados, fecha ascendente.
 *
 * Si Supabase no está configurado devuelve `[]` (misma degradación que el
 * resto de los repositorios de este panel).
 * @returns {Promise<Array<{ fecha: string, descripcion: string, created_at: string }>>}
 * @throws {Error} si Supabase responde con error
 */
export async function listFeriados() {
    const client = getSupabaseClient();
    if (!client) return [];

    const { data, error } = await client.from('feriados').select(FERIADO_COLUMNS).order('fecha', { ascending: true });
    if (error) rethrow(error);
    return data ?? [];
}

/**
 * Da de alta un feriado. `fecha` es la clave primaria (`YYYY-MM-DD`) — un
 * alta duplicada falla con el código de Postgres de violación de PK
 * (`23505`), que el caller puede distinguir vía `error.code`.
 * @param {{ fecha: string, descripcion: string }} params
 * @returns {Promise<{ fecha: string, descripcion: string, created_at: string }>}
 * @throws {Error} si Supabase no está configurado o responde con error
 */
export async function createFeriado({ fecha, descripcion }) {
    const client = getSupabaseClient();
    if (!client) throw new Error('Feriados no disponible');

    const { data, error } = await client
        .from('feriados')
        .insert({ fecha, descripcion })
        .select(FERIADO_COLUMNS)
        .single();

    if (error) rethrow(error);
    return data;
}

/**
 * Elimina un feriado por fecha. Los `liquidaciones` ya confirmados NO se ven
 * afectados: `multiplicador_feriado`/`detalle` quedan congelados en la fila
 * (AD9), así que borrar un feriado nunca reescribe una liquidación pasada.
 * @param {string} fecha
 * @returns {Promise<void>}
 * @throws {Error} si Supabase no está configurado o responde con error
 */
export async function deleteFeriado(fecha) {
    const client = getSupabaseClient();
    if (!client) throw new Error('Feriados no disponible');

    const { error } = await client.from('feriados').delete().eq('fecha', fecha);
    if (error) rethrow(error);
}
